import { useState, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/shared/empty-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { ProgressBar } from "@/components/shared/progress-bar";
import { useSearch, type SearchFilters } from "@/hooks/use-search";
import { useDebouncedCallback } from "@/hooks/use-debounce";
import type { SearchResult, ObjectiveStatus } from "@/types/entities";

function SearchResultsSkeleton() {
  return (
    <div className="space-y-3" role="status" aria-label="Loading search results">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-5 w-20" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-2 w-1/3" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SearchResultCard({ result }: { result: SearchResult }) {
  const isObjective = result.entity_type === "objective";
  const linkTo = isObjective ? "/objectives/$id" : "/key-results/$id";

  return (
    <Link
      to={linkTo}
      params={{ id: result.entity_id }}
      className="block"
      role="listitem"
    >
      <Card className="transition-colors hover:border-[hsl(var(--primary))]">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="shrink-0">
                  {isObjective ? "Objective" : "Key Result"}
                </Badge>
                <h3 className="truncate font-medium">{result.title}</h3>
              </div>
              {result.description && (
                <p className="line-clamp-2 text-sm text-[hsl(var(--muted-foreground))]">
                  {result.description}
                </p>
              )}
              {result.completion_percent != null && (
                <div className="flex items-center gap-2 pt-1">
                  <ProgressBar
                    value={result.completion_percent}
                    className="max-w-48"
                  />
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    {Math.round(result.completion_percent)}%
                  </span>
                </div>
              )}
            </div>
            <StatusBadge status={result.status as ObjectiveStatus} />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function SearchResultsList({
  results,
  nextCursor,
  onLoadMore,
}: {
  results: SearchResult[];
  nextCursor: string | null;
  onLoadMore: () => void;
}) {
  return (
    <>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">
        {results.length} result{results.length !== 1 ? "s" : ""}{nextCursor ? "+" : ""}
      </p>
      <div className="space-y-3" role="list" aria-label="Search results">
        {results.map((result) => (
          <SearchResultCard key={result.entity_id} result={result} />
        ))}
      </div>
      {nextCursor && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={onLoadMore}>
            Load More
          </Button>
        </div>
      )}
    </>
  );
}

function SearchContent({
  filters,
  isLoading,
  error,
  results,
  nextCursor,
  onLoadMore,
}: {
  filters: SearchFilters;
  isLoading: boolean;
  error: Error | null;
  results: SearchResult[];
  nextCursor: string | null;
  onLoadMore: () => void;
}) {
  if (filters.q.length === 0) {
    return (
      <EmptyState
        icon={<Search className="h-12 w-12" />}
        title="Search objectives and key results"
        description="Enter a search term to find objectives and key results by title or description."
      />
    );
  }
  if (isLoading) return <SearchResultsSkeleton />;
  if (error) return <EmptyState title="Search failed" description={error.message} />;
  if (results.length === 0) {
    return (
      <EmptyState
        icon={<Search className="h-12 w-12" />}
        title="No results found"
        description={`No matches for "${filters.q}". Try a different search term or filter.`}
      />
    );
  }
  return <SearchResultsList results={results} nextCursor={nextCursor} onLoadMore={onLoadMore} />;
}

export function SearchPage() {
  const [filters, setFilters] = useState<SearchFilters>({ q: "" });
  const [inputValue, setInputValue] = useState("");

  const { debouncedFn: debouncedSetQuery } = useDebouncedCallback(
    useCallback((value: string) => {
      setFilters((prev) => ({ ...prev, q: value, cursor: undefined }));
    }, []),
    300,
  );

  const { data, isLoading, error } = useSearch(filters);
  const results = data?.data ?? [];
  const nextCursor = data?.next_cursor ?? null;

  const handleInputChange = (value: string) => {
    setInputValue(value);
    debouncedSetQuery(value);
  };

  const handleEntityTypeChange = (value: string) => {
    const entityType = value === "all" ? undefined : (value as "objective" | "key_result");
    setFilters((prev) => ({ ...prev, entity_type: entityType, cursor: undefined }));
  };

  const loadMore = () => {
    if (nextCursor) {
      setFilters((prev) => ({ ...prev, cursor: nextCursor }));
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-semibold">Search</h1>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input
            placeholder="Search objectives and key results..."
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            className="pl-9"
            aria-label="Search query"
            autoFocus
          />
        </div>
        <Select
          value={filters.entity_type ?? "all"}
          onValueChange={handleEntityTypeChange}
        >
          <SelectTrigger className="w-full sm:w-40" aria-label="Filter by entity type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="objective">Objectives</SelectItem>
            <SelectItem value="key_result">Key Results</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <SearchContent
        filters={filters}
        isLoading={isLoading}
        error={error}
        results={results}
        nextCursor={nextCursor}
        onLoadMore={loadMore}
      />
    </div>
  );
}
